/**
 * LightweightSignupInline — the 4-field signup form that runs inside the
 * booking wizard when the user isn't already authed.
 *
 * Calls /api/auth/lightweight-signup which creates the account and
 * immediately issues the auth cookie, so the user can continue the
 * booking flow without a separate login step.
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import propertyCheckService from '../../services/propertyCheckService';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  countryCode: 'ZW',
};

const LightweightSignupInline = ({ onSuccess, onSwitchToLogin }) => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Client-side guards (server still validates)
    if (form.firstName.trim().length < 2) return setError('First name is required');
    if (form.lastName.trim().length < 2) return setError('Last name is required');
    if (!/.+@.+\..+/.test(form.email)) return setError('Please enter a valid email');
    if (form.phoneNumber.trim().length < 6) return setError('Phone number is required for SMS updates');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    if (!/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/\d/.test(form.password)) {
      return setError('Password must contain uppercase, lowercase, and a number');
    }

    setLoading(true);
    try {
      const result = await propertyCheckService.lightweightSignup(form);
      if (onSuccess) onSuccess(result);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Sign-up failed';
      const code = err?.response?.data?.code;
      if (code === 'EMAIL_ALREADY_REGISTERED') {
        setError('That email is already registered. Sign in to continue with your booking.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Create your account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We need an account to send you the report and updates. Takes about 30 seconds.
      </Typography>

      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="First name"
            value={form.firstName}
            onChange={update('firstName')}
            required
            autoComplete="given-name"
          />
          <TextField
            fullWidth
            label="Last name"
            value={form.lastName}
            onChange={update('lastName')}
            required
            autoComplete="family-name"
          />
        </Stack>
        <TextField
          fullWidth
          type="email"
          label="Email"
          value={form.email}
          onChange={update('email')}
          required
          autoComplete="email"
        />
        <TextField
          fullWidth
          label="Phone (with country code)"
          value={form.phoneNumber}
          onChange={update('phoneNumber')}
          required
          autoComplete="tel"
          placeholder="+263 77 123 4567"
          helperText="We'll send SMS updates when your verifier is on the way"
        />
        <TextField
          fullWidth
          type="password"
          label="Password"
          value={form.password}
          onChange={update('password')}
          required
          autoComplete="new-password"
          helperText="At least 8 characters with upper, lower, and a number"
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : 'Create account'}
        </Button>

        {onSwitchToLogin && (
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button onClick={onSwitchToLogin} size="small" sx={{ textTransform: 'none', p: 0 }}>
              Sign in
            </Button>
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default LightweightSignupInline;
